import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useFormik } from "formik";
import { useAuthStore } from '../store/authStore';
import axios from "axios";
import { useNavigate, useLocation } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { urlBase } from '../config';
import { logActivity } from '../services/ApiServices';
import { logActionType } from "../services/MyCollections";
import Copyright from '../components/Copyright';
import Pelikan_main_image from '../img/Pelikan_main_image.jpg';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  username: Yup.string().required('Login jest wymagany'),
  password: Yup.string().required('Hasło jest wymagane'),
});

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3B82F6',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
        },
      },
    },
  },
});

/* ── Inline styles ── */
const styles = {
  root: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  /* Left panel — animated gradient */
  left: {
    flex: 1,
    display: 'none',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    background: 'linear-gradient(135deg, #0F1C2E 0%, #1E3A5F 40%, #1E40AF 70%, #3B82F6 100%)',
    position: 'relative',
    overflow: 'hidden',
  },

  leftDecor1: {
    position: 'absolute',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%)',
    top: '-80px',
    right: '-80px',
    pointerEvents: 'none',
  },

  leftDecor2: {
    position: 'absolute',
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139,92,246,0.20) 0%, transparent 70%)',
    bottom: '-60px',
    left: '-60px',
    pointerEvents: 'none',
  },

  leftContent: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    maxWidth: 420,
  },

  leftImageWrapper: {
    width: 200,
    height: 200,
    borderRadius: '50%',
    overflow: 'hidden',
    margin: '0 auto 32px',
    boxShadow: '0 0 0 4px rgba(255,255,255,0.15), 0 20px 60px rgba(0,0,0,0.3)',
    border: '3px solid rgba(255,255,255,0.2)',
  },

  leftImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  leftTitle: {
    fontSize: 34,
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '-0.02em',
    marginBottom: 12,
    lineHeight: 1.2,
  },

  leftSubtitle: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.6,
    maxWidth: 320,
    margin: '0 auto',
  },

  leftBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 32,
    padding: '8px 18px',
    borderRadius: 99,
    background: 'rgba(255,255,255,0.10)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.18)',
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '0.02em',
  },

  /* Right panel — form */
  right: {
    width: '100%',
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 40px',
    background: '#F8FAFC',
    position: 'relative',
  },

  formCard: {
    width: '100%',
    maxWidth: 380,
    background: '#fff',
    borderRadius: 16,
    padding: '40px 36px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E2E8F0',
  },

  logoMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 28,
  },

  imageSmall: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    objectFit: 'cover',
    marginBottom: 12,
    boxShadow: '0 4px 16px rgba(59,130,246,0.20)',
    border: '2px solid #3B82F6',
  },

  formTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0F172A',
    letterSpacing: '-0.01em',
    marginBottom: 4,
    textAlign: 'center',
  },

  formSubtitle: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 28,
  },

  errorMsg: {
    color: '#EF4444',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 12,
    fontWeight: 500,
    textAlign: 'center',
  },

  submitBtn: {
    marginTop: 8,
    marginBottom: 4,
    height: 46,
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: '0.01em',
    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
    boxShadow: '0 4px 16px rgba(59,130,246,0.30)',
    transition: 'all 200ms ease',
    textTransform: 'none',
  },

  copyrightWrap: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
};

/* ── Responsive — show left panel on wider screens ── */
const mediaStyles = `
  @keyframes gradientShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes floatUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .signin-left {
    display: none !important;
  }
  .signin-right {
    max-width: 100% !important;
    min-height: 100vh;
  }
  @media (min-width: 768px) {
    .signin-left {
      display: flex !important;
    }
    .signin-right {
      max-width: 480px !important;
    }
    .signin-logo-mobile {
      display: none !important;
    }
  }
  .signin-form-card {
    animation: floatUp 350ms cubic-bezier(0.4, 0, 0.2, 1) both;
  }
`;

const SignIn = () => {
  const loginStore = useAuthStore(state => state.login);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const roles = useAuthStore(state => state.roles);
  const mfaSetupEnforced = useAuthStore(state => state.mfaSetupEnforced);
  const logoutStore = useAuthStore(state => state.logout);
  const userRolesList = Array.isArray(roles) ? roles : [];
  const hasValidRole = userRolesList.length > 0 && !userRolesList.includes('NO_PERMISSIONS');
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/MonthlyStatement";
  const [errMsg, setErrMsg] = useState('');
  const errRef = useRef();

  // Auto-redirect if user already has an active session in localStorage
  useEffect(() => {
    if (isAuthenticated) {
      if (!hasValidRole) {
        logoutStore();
        sessionStorage.clear();
        localStorage.removeItem('pelikan-auth');
        localStorage.removeItem('pelikan-last-activity');
        return;
      }
      if (mfaSetupEnforced) {
        navigate('/Settings?tab=security', { replace: true });
        return;
      }
      const userRoles = Array.isArray(roles) ? roles : [];
      if (!userRoles.includes('ACCOUNTANT')
        && !userRoles.includes('SUPERVISOR_ACCOUNTANT')
        && !userRoles.includes('CHIEF_ACCOUNTANT')) {
        navigate('/ClientsList', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, hasValidRole, roles, mfaSetupEnforced, navigate, from, logoutStore]);

  // 2FA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isSubmittingMfa, setIsSubmittingMfa] = useState(false);
  const [isBackupCodeMode, setIsBackupCodeMode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const handleSuccessfulAuth = (accessToken, refreshToken, deviceTrustToken, responseData = {}) => {
    if (deviceTrustToken) {
      localStorage.setItem('pelikan-device-token', deviceTrustToken);

    }
    const tokenDecoded = jwtDecode(accessToken);
    try { localStorage.setItem('pelikan-last-activity', Date.now().toString()); } catch (e) { }
    loginStore(accessToken, refreshToken, responseData);
    logActivity(logActionType.LOGIN, tokenDecoded.name, accessToken);
    if (responseData.mfaSetupEnforced || tokenDecoded.mfaSetupEnforced) {
      navigate('/Settings?tab=security', { replace: true });
      return;
    }
    if (!tokenDecoded.roles.includes('ACCOUNTANT')
      && !tokenDecoded.roles.includes('SUPERVISOR_ACCOUNTANT')
      && !tokenDecoded.roles.includes('CHIEF_ACCOUNTANT')) {
      navigate('/ClientsList', { replace: true });
    } else {
      navigate(from, { replace: true });
    }
  };

  const onSubmit = async (values) => {
    try {
      const storedDeviceToken = localStorage.getItem('pelikan-device-token');
      const response = await axios.post(
        urlBase + "/api/auth/login",
        {
          ...values,
          deviceTrustToken: storedDeviceToken || undefined,
        }
      );
      if (response.data.mfaRequired) {
        setMfaToken(response.data.mfaToken);
        setMfaRequired(true);
        setErrMsg('');
        return;
      }
      handleSuccessfulAuth(response.data.accessToken, response.data.refreshToken, response.data.deviceTrustToken, response.data);
    } catch (err) {
      if (!err?.response) {
        setErrMsg('Brak odpowiedzi serwera');
      } else if (err.response?.status === 423) {
        setErrMsg('Konto zostało tymczasowo zablokowane z powodu zbyt wielu nieudanych prób logowania. Spróbuj ponownie za 5 minut.');
      } else if (err.response?.status === 429) {
        setErrMsg('Zbyt wiele prób logowania z tego urządzenia. Odczekaj chwilę i spróbuj ponownie.');
      } else if (err.response?.status === 400) {
        setErrMsg('Wprowadź poprawny login i hasło');
      } else if (err.response?.status === 401) {
        setErrMsg('Błędny login lub hasło');
      } else if (err.response?.status === 403) {
        setErrMsg(typeof err.response?.data === 'string' && err.response.data
          ? err.response.data
          : 'Twoje konto nie posiada uprawnień do korzystania z systemu. Skontaktuj się z administratorem.');
      } else {
        setErrMsg('Wystąpił błąd logowania. Spróbuj ponownie później.');
      }
      errRef.current?.focus();
    }
  };

  const handleMfaSubmit = async (e) => {
    e?.preventDefault();
    if (!totpCode || totpCode.trim().length === 0) {
      setErrMsg(isBackupCodeMode ? 'Wprowadź kod zapasowy' : 'Wprowadź 6-cyfrowy kod z aplikacji');
      return;
    }
    setIsSubmittingMfa(true);
    setErrMsg('');
    try {
      const response = await axios.post(urlBase + "/api/auth/login/2fa", {
        mfaToken,
        code: totpCode.trim(),
        rememberDevice: rememberDevice,
      });
      handleSuccessfulAuth(response.data.accessToken, response.data.refreshToken, response.data.deviceTrustToken, response.data);
    } catch (err) {
      if (!err?.response) {
        setErrMsg('Brak odpowiedzi serwera');
      } else if (err.response?.status === 423) {
        setErrMsg('Konto zostało zablokowane z powodu zbyt wielu prób. Spróbuj ponownie za 5 minut.');
      } else if (typeof err.response?.data === 'string') {
        setErrMsg(err.response.data);
      } else {
        setErrMsg(err.response?.data?.message || 'Nieprawidłowy kod weryfikacyjny. Spróbuj ponownie.');
      }
      errRef.current?.focus();
    } finally {
      setIsSubmittingMfa(false);
    }
  };

  const handleCancelMfa = () => {
    setMfaRequired(false);
    setMfaToken('');
    setTotpCode('');
    setErrMsg('');
    setIsBackupCodeMode(false);
  };

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema,
    onSubmit,
  });

  if (isAuthenticated && hasValidRole) {
    return null;
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <style>{mediaStyles}</style>

      <div style={styles.root}>
        {/* ── Left panel (hidden on mobile) ── */}
        <div className="signin-left" style={styles.left}>
          {/* Decorative blobs */}
          <div style={styles.leftDecor1} />
          <div style={styles.leftDecor2} />

          <div style={styles.leftContent}>
            {/* Hero image */}
            <div style={styles.leftImageWrapper}>
              <img
                src={Pelikan_main_image}
                alt="Pelikan"
                style={styles.leftImage}
              />
            </div>

            <div style={styles.leftTitle}>System Pelikan</div>
            <div style={styles.leftSubtitle}>
              Platforma do zarządzania biurem rachunkowym firmy Seven PM.
            </div>

            <div style={styles.leftBadge}>
              ✦ &nbsp;Bezpieczne logowanie SSL
            </div>
          </div>
        </div>

        {/* ── Right panel — form ── */}
        <div className="signin-right" style={styles.right}>
          <div className="signin-form-card" style={styles.formCard}>

            {/* Logo visible only on mobile */}
            <div className="signin-logo-mobile" style={styles.logoMobile}>
              <img
                src={Pelikan_main_image}
                alt="Pelikan"
                style={styles.imageSmall}
              />
            </div>

            <Typography style={styles.formTitle}>
              {mfaRequired ? 'Weryfikacja 2FA' : 'Zaloguj się'}
            </Typography>
            <Typography style={styles.formSubtitle}>
              {mfaRequired
                ? (isBackupCodeMode
                  ? 'Wprowadź jednorazowy kod zapasowy'
                  : 'Wprowadź 6-cyfrowy kod z aplikacji Google Authenticator')
                : 'Witaj ponownie w Systemie Pelikan'}
            </Typography>

            {/* Error message */}
            {errMsg && (
              <div ref={errRef} style={styles.errorMsg} aria-live="assertive">
                {errMsg}
              </div>
            )}

            {/* 2FA Form vs Password Form */}
            {mfaRequired ? (
              <Box component="form" onSubmit={handleMfaSubmit} noValidate>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      fontSize: 24,
                      boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
                    }}
                  >
                    <i className={isBackupCodeMode ? "fas fa-key" : "fas fa-mobile-alt"} />
                  </Box>
                </Box>

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="totpCode"
                  label={isBackupCodeMode ? "Kod zapasowy" : "Kod weryfikacyjny (6 cyfr)"}
                  name="totpCode"
                  autoFocus
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  placeholder={isBackupCodeMode ? "np. 8-znakowy kod" : "000 000"}
                  inputProps={{
                    maxLength: isBackupCodeMode ? 20 : 8,
                    style: {
                      textAlign: 'center',
                      letterSpacing: isBackupCodeMode ? '2px' : '6px',
                      fontSize: '22px',
                      fontWeight: '700',
                      fontFamily: 'monospace',
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                      size="small"
                      sx={{
                        color: '#3B82F6',
                        '&.Mui-checked': { color: '#2563EB' },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, color: '#475569', userSelect: 'none' }}>
                      Zapamiętaj to urządzenie na 30 dni
                    </Typography>
                  }
                  sx={{ mt: 1, mb: 0.5, width: '100%', justifyContent: 'flex-start' }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmittingMfa}
                  style={styles.submitBtn}
                  sx={{ mt: 2, mb: 1 }}
                >
                  {isSubmittingMfa ? 'Weryfikacja…' : 'Zatwierdź i zaloguj'}
                </Button>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5, textAlign: 'center' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setIsBackupCodeMode(!isBackupCodeMode);
                      setTotpCode('');
                      setErrMsg('');
                    }}
                    sx={{ textTransform: 'none', fontSize: 13, color: '#3B82F6' }}
                  >
                    {isBackupCodeMode
                      ? '← Użyj kodu z Google Authenticator'
                      : 'Nie masz dostępu do telefonu? Użyj kodu zapasowego'}
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    onClick={handleCancelMfa}
                    sx={{ textTransform: 'none', fontSize: 13, color: '#64748B' }}
                  >
                    Wróć do logowania loginem i hasłem
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Login"
                  name="username"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.username}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                  autoFocus
                  autoComplete="username"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Hasło"
                  type="password"
                  id="password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  autoComplete="current-password"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={formik.isSubmitting}
                  style={styles.submitBtn}
                  sx={{ mt: 2, mb: 1 }}
                >
                  {formik.isSubmitting ? 'Logowanie…' : 'Zaloguj się'}
                </Button>
              </Box>
            )}
          </div>

          <div style={styles.copyrightWrap}>
            <Copyright sx={{ mt: 2, mb: 1 }} />
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default SignIn;
